function waitUntilElementIsRendered(elementOrSelector, timeout) {
  timeout = typeof timeout === "number" ? timeout : 10000;

  return new Promise((resolve) => {
    const startTime = window.performance.now();

    const checkElement = () => {
      if (window.performance.now() - startTime >= timeout) {
        resolve(null);
        return;
      }

      const element =
        typeof elementOrSelector === "string"
          ? document.querySelector(elementOrSelector)
          : elementOrSelector;

      if (element) {
        resolve(element);
        return;
      }

      window.requestAnimationFrame(checkElement);
    };

    window.requestAnimationFrame(checkElement);
  });
}

class ProductInventory extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this._init();
    document.addEventListener("variant:changed", this._updateMessaging.bind(this));
    document.addEventListener("inventoryInfo.appRendered", this._updateMessaging.bind(this));
  }

  _init() {
    this._setLocationObject();
  }

  _setLocationObject() {
    const handle = inventoryInfo?.api?.getProductIdentifiers()?.handle;

    if (inventoryInfo && handle && inventoryInfo[handle]?.variantLocations) {
      window.variantLocations = inventoryInfo[handle].variantLocations;
      const currentVariantId = parseInt(this.dataset.currentVariantId, 10);
      const locationObject = this._getLocationObject(currentVariantId);
      this._getMessaging(locationObject);
    } else {
      setTimeout(this._setLocationObject.bind(this), 250);
    }
  }

  _updateMessaging(e) {
    const variantId = e.detail?.id ?? inventoryInfo?.api?.getProductIdentifiers()?.variant;

    if (variantId == null) return;

    this.dataset.currentVariantId = variantId;
    const locationObject = this._getLocationObject(variantId);
    this._getMessaging(locationObject);
  }

  _getLocationObject(variantId) {
    if (!window.variantLocations) {
      setTimeout(this._setLocationObject.bind(this), 250);
      return null;
    }

    return window.variantLocations.find((loc) => loc.variant == variantId) || null;
  }

  _setInventoryNote(html) {
    waitUntilElementIsRendered(".iia-container").then((container) => {
      if (!container) return;

      let noteEl = container.querySelector(".inventoryNote");

      if (!noteEl) {
        noteEl = document.createElement("div");
        noteEl.className = "inventoryNote";
        container.appendChild(noteEl);
      }

      noteEl.innerHTML = html;
    });
  }

  _clearInventoryNote() {
    const noteEl = document.querySelector(".iia-container .inventoryNote");
    if (noteEl) noteEl.innerHTML = "";
  }

  _buildSupplierNoteHtml(label) {
    return `<span class="is">
      <i class="fa-sharp fa-solid fa-circle-check" aria-hidden="true"></i>
      ${label}
    </span>`;
  }

  _getMessaging(locationObject) {
    if (!locationObject) return;

    const { variantSupplier } = this.dataset;
    const locations = locationObject.inventoryLocations;

    for (const { quantity, location } of locations) {
      if (quantity <= 0) continue;

      this.classList.add("show");

      const isPhysicalStore = location.name === "In Stock";

      if (isPhysicalStore) {
        // Physical store stock — messaging varies by supplier
        if (variantSupplier === "RALEIGH" || variantSupplier === "HAIBIKE") {
          this.querySelector(".message").innerText = window.variantStrings.altStockText;
        } else {
          // TREK and all others use the location note
          this.querySelector(".message").innerText = location.note;
        }
        this._clearInventoryNote();
        return;
      }

      // Supplier warehouse stock — messaging varies by supplier
      if (variantSupplier === "TREK") {
        this.querySelector(".message").innerText = window.variantStrings.trekStockText;
        this._setInventoryNote(this._buildSupplierNoteHtml("In stock at supplier warehouse"));
      } else if (variantSupplier === "RALEIGH" || variantSupplier === "HAIBIKE") {
        this.querySelector(".message").innerText = window.variantStrings.altStockText;
        this._setInventoryNote(this._buildSupplierNoteHtml(location.name));
      } else {
        // All other suppliers
        this.querySelector(".message").innerText = location.note;
        this._setInventoryNote(this._buildSupplierNoteHtml(location.name));
      }

      return;
    }

    // No location had stock
    this.classList.remove("show");
    this._clearInventoryNote();
  }
}

customElements.define("product-inventory", ProductInventory);